import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Users, Gamepad2, Activity, AlertTriangle, TrendingUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { apiFetch } from "../../utils/api";
import { Summary } from "./types";
import {
  API, qs, isoDay, daysAgo, errMsg, fmtAmt, fmtInt, fmtDate, pnlColor,
  Header, DateRange, TestToggle, ErrorBox, Panel, EmptyRow, PlayerCell,
  inputCls, thCls, thR, tdCls, tdR, theadRow, tbodyRow, pageCls, chipCls,
} from "./shared";

const holdPct = (ggr: number | string, wagered: number | string) =>
  Number(wagered) ? ((Number(ggr) / Number(wagered)) * 100).toFixed(2) + "%" : "—";

export default function Dashboard() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(isoDay(new Date()));
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartCur, setChartCur] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiFetch<Summary>(API + "/summary" + qs({ from, to, include_test: includeTest })));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [from, to, includeTest]);
  useEffect(() => { load(); }, [load]);

  const currencies = useMemo(() => (data ? data.by_currency.map((c) => c.currency) : []), [data]);
  const activeCur = chartCur && currencies.includes(chartCur) ? chartCur : currencies[0] || "";
  const chartData = useMemo(() => (data ? data.daily.filter((d) => d.currency === activeCur).map((d) => ({
    day: d.day.slice(5), Wagered: Number(d.wagered), Payout: Number(d.payout), GGR: Number(d.ggr),
  })) : []), [data, activeCur]);

  const t = data ? data.totals : null;
  const cb = data ? data.callbacks : null;
  const cards = t ? [
    { label: "Players", value: fmtInt(t.players), icon: <Users size={22} />, gradient: "from-[#2B6EF5] to-[#8B5CF6]" },
    { label: "Rounds / Bets", value: `${fmtInt(t.rounds)} / ${fmtInt(t.bets)}`, icon: <Gamepad2 size={22} />, gradient: "from-[#0EA5E9] to-[#06B6D4]" },
    { label: "Sessions Launched", value: fmtInt(t.sessions), icon: <Activity size={22} />, gradient: "from-[#10B981] to-[#059669]" },
    { label: "Refused / Rollbacks", value: `${fmtInt(t.refused)} / ${fmtInt(t.rollbacks)}`, icon: <AlertTriangle size={22} />, gradient: "from-[#F59E0B] to-[#EF4444]" },
  ] : [];

  return (
    <div className={pageCls}>
      <Header title="Original Games — Dashboard" subtitle="Performance of the Original Games provider, per currency" onRefresh={load} loading={loading}>
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} />
        <TestToggle value={includeTest} onChange={setIncludeTest} />
      </Header>
      <ErrorBox error={error} />

      {data && (
        <div className="flex flex-wrap gap-2 text-xs">
          <span className={chipCls}>Environment: <b>{data.environment}</b></span>
          <span className={chipCls}>Mode: <b>{data.mode}</b></span>
          {cb && (
            <span className={chipCls}>
              Callbacks: <b className="text-[#0ECC68]">{fmtInt(cb.accepted)} accepted</b> · <b className={Number(cb.rejected) ? "text-[#E01B4F]" : ""}>{fmtInt(cb.rejected)} rejected</b> · last {fmtDate(cb.last_callback_at)}
            </span>
          )}
          {data.test_users_excluded.length > 0 && <span className={chipCls + " text-[#878AA2]"}>Test players hidden: {data.test_users_excluded.join(", ")}</span>}
        </div>
      )}

      {t && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.label} className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${c.gradient} p-5`}>
              <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
              <div className="flex justify-between items-start relative z-10">
                <div>
                  <p className="text-[#F9F9F9]/70 text-xs uppercase tracking-wider font-medium">{c.label}</p>
                  <p className="text-3xl font-extrabold mt-2 tracking-tight">{c.value}</p>
                </div>
                <div className="bg-white/20 p-2.5 rounded-lg">{c.icon}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <Panel title="By currency">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className={theadRow}>
                <th className={thCls}>Currency</th><th className={thR}>Bets</th><th className={thR}>Wins</th><th className={thR}>Players</th>
                <th className={thR}>Wagered</th><th className={thR}>Payout</th><th className={thR}>Rollbacks (net)</th><th className={thR}>GGR</th><th className={thR}>Hold %</th>
              </tr></thead>
              <tbody>
                {data.by_currency.map((c) => (
                  <tr key={c.currency} className={tbodyRow}>
                    <td className={tdCls + " font-semibold"}>{c.currency}</td>
                    <td className={tdR}>{fmtInt(c.bets)}</td>
                    <td className={tdR}>{fmtInt(c.wins)}</td>
                    <td className={tdR}>{fmtInt(c.players)}</td>
                    <td className={tdR + " text-[#4A85F7]"}>{fmtAmt(c.wagered)}</td>
                    <td className={tdR + " text-[#0ECC68]"}>{fmtAmt(c.payout)}</td>
                    <td className={tdR + " text-[#FFC23F]"}>{fmtAmt(c.rollback_net)}</td>
                    <td className={`${tdR} font-semibold ${pnlColor(c.ggr)}`}>{fmtAmt(c.ggr)}</td>
                    <td className={tdR + " text-[#878AA2]"}>{holdPct(c.ggr, c.wagered)}</td>
                  </tr>
                ))}
                {data.by_currency.length === 0 && <EmptyRow cols={9} text="No activity in this range" />}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-xs text-[#878AA2] border-t border-[#1E2D55]">GGR = wagered − payout − net rollbacks (house view). Amounts are never added across currencies.</p>
        </Panel>
      )}

      {data && currencies.length > 0 && (
        <Panel
          title={<span className="flex items-center gap-2"><TrendingUp size={18} />Daily activity</span>}
          right={<select value={activeCur} onChange={(e) => setChartCur(e.target.value)} className={inputCls}>
            {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>}
        >
          <div className="p-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#1E2D55" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#878AA2" fontSize={12} />
                <YAxis stroke="#878AA2" fontSize={12} />
                <Tooltip contentStyle={{ background: "#0C0D1D", border: "1px solid #1E2D55", color: "#F9F9F9" }} />
                <Legend />
                <Line type="monotone" dataKey="Wagered" stroke="#4A85F7" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Payout" stroke="#0ECC68" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="GGR" stroke="#FFC23F" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}

      {data && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Panel title="By game">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className={theadRow}>
                  <th className={thCls}>Game</th><th className={thCls}>Cur.</th><th className={thR}>Bets</th>
                  <th className={thR}>Players</th><th className={thR}>Wagered</th><th className={thR}>GGR</th>
                </tr></thead>
                <tbody>
                  {data.by_game.map((g) => (
                    <tr key={`${g.game_code}-${g.currency}`} className={tbodyRow}>
                      <td className={tdCls + " font-medium capitalize"}>{g.game_code || "—"}</td>
                      <td className={tdCls + " text-[#878AA2]"}>{g.currency}</td>
                      <td className={tdR}>{fmtInt(g.bets)}</td>
                      <td className={tdR}>{fmtInt(g.players)}</td>
                      <td className={tdR + " text-[#4A85F7]"}>{fmtAmt(g.wagered)}</td>
                      <td className={`${tdR} ${pnlColor(g.ggr)}`}>{fmtAmt(g.ggr)}</td>
                    </tr>
                  ))}
                  {data.by_game.length === 0 && <EmptyRow cols={6} text="No data" />}
                </tbody>
              </table>
            </div>
          </Panel>
          <Panel title="Top players (by wagered)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className={theadRow}>
                  <th className={thCls}>Player</th><th className={thCls}>Cur.</th><th className={thR}>Bets</th>
                  <th className={thR}>Wagered</th><th className={thR}>Payout</th><th className={thR}>Player P/L</th>
                </tr></thead>
                <tbody>
                  {data.top_players.map((p) => (
                    <tr key={`${p.user_id}-${p.currency}`} className={tbodyRow}>
                      <PlayerCell name={p.name} id={p.user_id} />
                      <td className={tdCls + " text-[#878AA2]"}>{p.currency}</td>
                      <td className={tdR}>{fmtInt(p.bets)}</td>
                      <td className={tdR + " text-[#4A85F7]"}>{fmtAmt(p.wagered)}</td>
                      <td className={tdR + " text-[#0ECC68]"}>{fmtAmt(p.payout)}</td>
                      <td className={`${tdR} ${pnlColor(-Number(p.ggr))}`}>{fmtAmt(-Number(p.ggr))}</td>
                    </tr>
                  ))}
                  {data.top_players.length === 0 && <EmptyRow cols={6} text="No data" />}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
